import React from 'react';
import { injectIntl } from 'react-intl';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { navy, grey10, grey25, grey75, white } from '@zocdoc/branding/colors';

import {
    INPUT_BORDER_WIDTH,
    INPUT_HEIGHT,
    INPUT_X_AFFORDANCE_DESKTOP,
    INPUT_X_AFFORDANCE_MOBILE,
} from '../../picker-constants';

import { FontFamily } from '../../typography';
import { breakpoint } from '../../grid';
import { Close } from '../../icons';

class SearchTextBox extends React.PureComponent {
    static propTypes = {
        'data-test': PropTypes.string,
        className: PropTypes.string,
        disabled: PropTypes.bool,
        value: PropTypes.string,
        name: PropTypes.string,
        placeholder: PropTypes.string,
        onChange: PropTypes.func,
        onBlur: PropTypes.func,
        onFocus: PropTypes.func,
        onKeyDown: PropTypes.func,
        clearTextHandler: PropTypes.func,
    };

    static defaultProps = {
        'data-test': 'search-textbox',
    };

    render() {
        const {
            'data-test': dataTest,
            className,
            clearTextHandler,
            disabled,
            inputCallbackRef,
            intl,
            name,
            onBlur,
            onChange,
            onFocus,
            onKeyDown,
            placeholder,
            textBoxFocused,
            value,
        } = this.props;

        const internationalizedPlaceholder = placeholder ?
            intl.formatMessage({
                id: 'UniversalSearch.Placeholder',
                defaultMessage: this.props.placeholder,
            }) : undefined;

        // this is to address buggy behavior in iOS safari where placeholder somehow appears under value
        // after user has chosen a browse specialty
        const placeholderText = value && value.length > 0 ? '' : internationalizedPlaceholder;

        const showCloseIcon = textBoxFocused && value;

        return (
            <SearchTextBoxWrapper textBoxFocused={this.props.textBoxFocused}>
                <StyledInput
                    autoComplete="off"
                    className={className}
                    data-prod-guids="universal-search-text-box:[4c2b7f86,6bca52f5]"
                    data-test={dataTest}
                    disabled={disabled}
                    innerRef={inputCallbackRef}
                    name={name}
                    onBlur={onBlur}
                    onChange={onChange}
                    onFocus={onFocus}
                    onInput={onChange}
                    onKeyDown={onKeyDown}
                    placeholder={placeholderText}
                    showCloseIcon={showCloseIcon}
                    type="text"
                    value={value}
                />
                <CloseWrapper
                    hidden={!showCloseIcon}
                    onClick={clearTextHandler}
                >
                    <Close height={10} />
                </CloseWrapper>
            </SearchTextBoxWrapper>
        );
    }
}

const SearchTextBoxWrapper = styled.div`
    ${breakpoint('xsmall', { down: true })} {
        padding-right: ${({ textBoxFocused }) => textBoxFocused ? 65 : 0}px;
        border-bottom: ${({ textBoxFocused }) => textBoxFocused ? `solid 1px ${grey10}` : 'none'};
    }
`;

const CloseWrapper = styled.span`
    align-items: center;
    cursor: pointer;
    display: ${p => p.hidden ? 'none' : 'inline-flex'};
    position: absolute;
    z-index: 1;
    top: 0px;
    right: 69px;
    padding: 19px 10px 18px 15px;

    ${breakpoint.small} {
        display: none;
    }
`;

const StyledInput = styled.input`
    appearance: none;
    background-color: ${white};
    border: ${INPUT_BORDER_WIDTH}px solid ${white};
    border-radius: 0;
    box-sizing: border-box;
    color: ${navy};
    font-family: ${FontFamily.SharpSansSemibold};
    font-size: 15px;
    height: ${INPUT_HEIGHT}px;
    padding: 0 ${INPUT_X_AFFORDANCE_MOBILE}px;
    text-overflow: ellipsis;
    text-size-adjust: none;
    width: 100%;

    &[disabled] {
        color: ${grey25};
    }

    &::placeholder {
        color: ${grey75};
    }

    &:focus::placeholder {
        color: ${grey25};
    }

    ${breakpoint('xsmall', { down: true })} {
        padding-right: ${p => p.showCloseIcon ? 35 : 12}px;
    }

    ${breakpoint.small} {
        padding-left: ${INPUT_X_AFFORDANCE_DESKTOP}px;
        padding-right: ${INPUT_X_AFFORDANCE_DESKTOP}px;
    }
`;

export default injectIntl(SearchTextBox);
